// HomeContent.jsx
import React, { useEffect, useState } from 'react';
import {
  FaGraduationCap,
  FaQuestionCircle,
  FaTimes,
  FaStar,
  FaChartBar,
  FaExternalLinkAlt,
  FaBook,
  FaCalendarAlt,
  FaBuilding,
  FaIdCard,
  FaUniversity
} from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getData } from '../../../../services/apiService';
import { setUser } from '../../../../store/userSlice';
import { motion, AnimatePresence } from 'framer-motion';
import Events from './Events';

const HomeContent = () => {
  const dispatch = useDispatch();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [selectedSubjectResult, setSelectedSubjectResult] = useState(null);
  const [apiResponse, setApiResponse] = useState({});
  const [loading, setLoading] = useState(true);
  const currentUser = useSelector((state) => state.user.user);
  const userName = currentUser?.user_name || 'User';
  const rollNo = currentUser?.roll_no || null;
  // HomeContent.js - Updated useEffect
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getData(`/AttendanceDefaulters/GetStudentDashboard?rollNo=${rollNo}`);

        // Check if response has error
        if (response?.isError) {
          console.error('API Error:', response.message);
          // If unauthorized, redirect to login (logout already happened)
          if (response.message?.includes('Unauthorized') || response.message?.includes('auth')) {
            // Don't redirect if already on login page
            if (!window.location.pathname.includes('/')) {
              window.location.href = '/';
            }
          }
          return;
        }

        // Check if we have valid data
        if (response?.data?.dashboard) {
          const dashboard = response.data.dashboard;
          setApiResponse(response);

          // Merge dashboard data with existing user data
          dispatch(setUser({
            user: {
              batch: dashboard?.batch,
              procYear: dashboard?.procYear,
              procSem: dashboard?.procSem,
              semNo: dashboard?.semNo,
              ...response.data // This will add all the data
            }
          }));
        } else {
          console.warn('No dashboard data received');
        }
      } catch (error) {
        console.error('Error fetching dashboard:', error);
        // Check if it's an auth error
        if (error?.response?.status === 401 || error?.message?.includes('401')) {
          // Logout already happened in apiService
          if (!window.location.pathname.includes('/')) {
            window.location.href = '/';
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);
  // Extract data from API response
  const dashboard = apiResponse?.data?.dashboard || {};
  const examTimeTable = apiResponse?.data?.examTimeTable || [];
  const subjectResults = apiResponse?.data?.subjectResults || [];
  const semesterSummary = apiResponse?.data?.semesterSummary || [];
  const nextExamDate = apiResponse?.data?.nextExamDate || '';

  // Process events from examTimeTable
  const events = examTimeTable.map((exam, index) => ({
    id: index + 1,
    title: exam.subjName || 'Unknown Subject',
    type: 'exam',
    date: exam.examDate || new Date().toISOString(),
    time: exam.examSession === 'FN' ? (exam.foreNoon || 'Morning Session') : (exam.afterNoon || 'Afternoon Session'),
    location: exam.instName || 'College Campus',
    subject: exam.subjName || 'Unknown',
    subjectCode: exam.subjCode || 'N/A',
    description: `${exam.subjName || 'Unknown'} examination for Semester ${exam.semNo || 'N/A'}`
  }));

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const options = { month: 'short', day: 'numeric', year: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Get grade color
  const getGradeColor = (grade) => {
    if (!grade) return 'text-gray-600 bg-gray-100';
    const colors = {
      'A+': 'text-emerald-600 bg-emerald-100',
      'A': 'text-blue-600 bg-blue-100',
      'B+': 'text-indigo-600 bg-indigo-100',
      'B': 'text-purple-600 bg-purple-100',
      'C': 'text-yellow-600 bg-yellow-100',
      'D': 'text-orange-600 bg-orange-100',
      'F': 'text-red-600 bg-red-100',
      'PASS': 'text-emerald-600 bg-emerald-100',
      'FAIL': 'text-red-600 bg-red-100'
    };
    return colors[grade] || 'text-gray-600 bg-gray-100';
  };

  const closeModal = () => {
    setSelectedEvent(null);
    setShowAllEvents(false);
    setSelectedSubjectResult(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-teal-50/30 to-blue-50/30 p-4 md:p-6 lg:p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-teal-50/30 to-blue-50/30 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">

        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl p-6 md:p-8 mb-6 shadow-2xl shadow-teal-900/20 relative overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, #0d4f5c 0%, #1a7a8a 100%)'
          }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>

          <div className="relative">
            <div className="flex flex-col md:flex-row items-start justify-between gap-4">
              <div className="flex-1">
                <h2 className="flex items-center gap-3 text-2xl md:text-xl font-bold text-white">
                  <FaGraduationCap className="text-xl" />
                  Welcome Back,<span className='text-teal-200'> {userName || 'Student'}!</span>
                </h2>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <span className="bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-medium text-white flex items-center gap-2">
                    <FaIdCard className="text-white/70" />
                    {dashboard.rollNo || 'N/A'}
                  </span>
                  <span className="bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-medium text-white flex items-center gap-2">
                    <FaBuilding className="text-white/70" />
                    {dashboard.courseName || 'N/A'}
                  </span>
                  <span className="bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-medium text-white">
                    Semester {dashboard.semNo || 'N/A'}
                  </span>
                  <span className="bg-yellow-400/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-medium text-yellow-200 flex items-center gap-1">
                    <FaStar className="text-yellow-300" />
                    {(dashboard.overallPercentage || 0).toFixed(1)}% Attendance
                  </span>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                <p className="text-white/80 text-xs">Next Exam</p>
                <p className="text-white font-bold text-lg">
                  {nextExamDate ? formatShortDate(nextExamDate) : 'No exams scheduled'}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards - Updated with Current Semester, Attendance, instName, courseName */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            {
              label: 'Current Semester',
              value: `Sem ${dashboard.semNo || 'N/A'}`,
              icon: <FaGraduationCap />,
              color: 'from-purple-400 to-purple-600',
              detail: dashboard.courseName?.split('(')[0]?.trim() || 'N/A'
            },
            {
              label: 'Attendance',
              value: `${(dashboard.overallPercentage || 0).toFixed(1)}%`,
              icon: <FaChartBar />,
              color: 'from-blue-400 to-blue-600',
              detail: `${dashboard.presentHours || 0}/${dashboard.totalHours || 0} hours`
            },
            {
              label: 'Institution',
              value: dashboard.instName?.split(',')[0]?.trim() || 'N/A',
              icon: <FaUniversity />,
              color: 'from-green-400 to-green-600',
              detail: dashboard.instName?.split(',')[1]?.trim() || ''
            },
            {
              label: 'Course',
              value: dashboard.courseName?.split('(')[0]?.trim() || 'N/A',
              icon: <FaBook />,
              color: 'from-orange-400 to-orange-600',
              detail: dashboard.courseName?.match(/\((.*?)\)/)?.[1] || 'Full Time'
            },
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -4, scale: 1.02 }}
              className="bg-white rounded-2xl p-4 shadow-lg shadow-teal-900/5 hover:shadow-xl transition-all duration-300 border border-gray-100/50"
            >
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center text-white text-lg shadow-lg flex-shrink-0`}>
                  {stat.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-gray-500 text-xs font-medium uppercase tracking-wider truncate">{stat.label}</p>
                  <p className="text-lg font-bold text-gray-800 truncate">{stat.value}</p>
                  <p className="text-xs text-gray-400 truncate">{stat.detail}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Exam Timetable Section */}
        {examTimeTable.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-white rounded-3xl p-6 mb-6 shadow-xl shadow-teal-900/5 border border-gray-100/50"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <FaCalendarAlt className="text-teal-600" />
                Upcoming Examinations
              </h3>
              <span className="text-sm bg-teal-100 text-teal-700 px-3 py-1 rounded-full font-medium">
                {examTimeTable.length} exams
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {examTimeTable.slice(0, 6).map((exam, index) => (
                <motion.div
                  key={exam.subjCode || index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-gray-50 to-teal-50/30 rounded-xl p-4 border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">{exam.subjName || 'Unknown Subject'}</p>
                      <p className="text-xs text-gray-500">{exam.subjCode || 'N/A'}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${exam.examSession === 'FN' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                      {exam.examSession === 'FN' ? 'Morning' : 'Afternoon'}
                    </span>
                  </div>
                  <div className="mt-2 space-y-1 text-xs text-gray-600">
                    <p><strong>Date:</strong> {formatDate(exam.examDate)}</p>
                    <p><strong>Day:</strong> {exam.examDay || 'N/A'}</p>
                    <p><strong>Time:</strong> {exam.examSession === 'FN' ? (exam.foreNoon || 'N/A') : (exam.afterNoon || 'N/A')}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Events Section */}
        {events.length > 0 && (
          <div className="mb-6">
            <Events
              events={events}
              onViewAll={() => setShowAllEvents(true)}
              onSelect={setSelectedEvent}
            />
          </div>
        )}

        {/* Semester Results Section */}
        {semesterSummary.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="bg-white rounded-3xl p-6 mb-6 shadow-xl shadow-teal-900/5 border border-gray-100/50"
          >
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FaChartBar className="text-teal-600" />
              Semester Performance
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {semesterSummary.map((sem, index) => (
                <motion.div
                  key={sem.semNo || index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-xl p-4 border border-gray-100"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Semester {sem.semNo || 'N/A'}</p>
                      <p className="text-2xl font-bold text-gray-800">{sem.sgpa || '0'}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${getGradeColor(sem.sGrade)}`}>
                      {sem.sGrade || 'N/A'}
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    <p>Credits Earned: {sem.semesterCreditsEarned || 0}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Subject Results */}
            {subjectResults.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Subject Details</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-2">
                  {subjectResults.map((subject, index) => (
                    <div key={subject.subjCode || index} className="bg-gray-50 rounded-xl p-3 border border-gray-100 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{subject.name || 'Unknown'}</p>
                        <p className="text-xs text-gray-500">{subject.subjCode || 'N/A'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getGradeColor(subject.letterGrade)}`}>
                          {subject.letterGrade || 'N/A'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${subject.result === 'PASS' ? 'bg-emerald-100 text-emerald-700' : subject.result === 'FAIL' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                          {subject.result || 'N/A'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Help Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-3xl p-6 md:p-8 border-2 border-teal-100/50 shadow-xl shadow-teal-900/5"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0d4f5c] to-[#1a7a8a] flex items-center justify-center text-white text-2xl shadow-lg flex-shrink-0">
                <FaQuestionCircle />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800">Need Help with Exams or Fees?</h3>
                <p className="text-gray-600 text-sm">Contact exam cell or fee department for assistance</p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] hover:shadow-xl text-white font-semibold py-3 px-8 rounded-2xl transition-all duration-300 flex items-center gap-2 group whitespace-nowrap"
            >
              <span>Contact Support</span>
              <FaExternalLinkAlt className="text-sm group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </div>
        </motion.div>

        {/* Modals */}
        <AnimatePresence>
          {(selectedEvent || showAllEvents || selectedSubjectResult) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 backdrop-blur-sm bg-black/50 flex items-center justify-center p-4 z-50"
              onClick={closeModal}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.9, y: 20, opacity: 0 }}
                className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] p-6 flex justify-between items-center sticky top-0">
                  <h3 className="text-xl font-bold text-white">
                    {selectedEvent ? selectedEvent.title :
                      showAllEvents ? 'All Events' : 'Subject Details'}
                  </h3>
                  <button
                    onClick={closeModal}
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 duration-200 flex items-center justify-center text-white hover:rotate-90 transform transition-transform"
                  >
                    <FaTimes className="text-xl" />
                  </button>
                </div>

                <div className="p-6 overflow-y-auto max-h-[70vh]">
                  {selectedEvent && (
                    <div>
                      <div className="flex flex-wrap items-center gap-2 mb-4">
                        <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-orange-100 text-orange-700">
                          📝 Exam
                        </span>
                        <span className="text-sm bg-teal-100 text-teal-800 px-3 py-1.5 rounded-full font-medium">
                          {formatDate(selectedEvent.date)}
                        </span>
                      </div>

                      <div className="space-y-3 text-sm text-gray-700">
                        <p><strong>Time:</strong> {selectedEvent.time}</p>
                        {selectedEvent.location && (
                          <p><strong>Location:</strong> {selectedEvent.location}</p>
                        )}
                        {selectedEvent.subject && (
                          <p><strong>Subject:</strong> {selectedEvent.subject} ({selectedEvent.subjectCode})</p>
                        )}
                        <div className="mt-3 p-4 bg-gray-50 rounded-xl">
                          <p>{selectedEvent.description}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {showAllEvents && (
                    <div className="space-y-6">
                      {events.map((event, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-2xl p-5 hover:bg-purple-50/50 transition-all duration-200 border-l-4 border-orange-400">
                          <div className="flex flex-wrap justify-between items-start gap-2">
                            <h4 className="font-semibold text-gray-800">{event.title}</h4>
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-orange-100 text-orange-700">
                              Exam
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-gray-500">
                            <span>{formatDate(event.date)}</span>
                            <span>•</span>
                            <span>{event.time}</span>
                            {event.location && (
                              <>
                                <span>•</span>
                                <span>{event.location}</span>
                              </>
                            )}
                          </div>
                          {event.subject && (
                            <p className="text-xs text-gray-600 mt-1">
                              <strong>Subject:</strong> {event.subject} ({event.subjectCode})
                            </p>
                          )}
                          <p className="text-sm text-gray-600 mt-2">{event.description}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default HomeContent;